import type { Column } from "@shared/types/data-table"
import type { Order } from "@features/in-stores/types/order.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import { 
  createStatusColumn,
  createTextColumn,
  createDateColumn,
  createCustomColumn,
  createIdColumn
} from "@shared/lib/tables/columns/column-factories"
import { CurrencyCell } from "@shared/lib/tables/cells/common-cells"

export const ORDER_COLUMNS: Record<string, Column<Order>> = {
  id: createIdColumn<Order>(),
  totalAmount: createCustomColumn<Order>(
    "totalAmount", 
    "Amount",
    120,
    (order: Order) => (
      <CurrencyCell
        amount={order.totalAmount}
        currency='USD'
        align="left"
        className="text-sm min-w-[88px]"
      />
    )
  ),
  status: createStatusColumn<Order>("order"),
  description: createTextColumn<Order>(
    "description",
    "Description",
    200,
    "group-hover:text-foreground"
  ),
  itemCount: createTextColumn<Order>(
    "itemCount",
    "Items",
    120,
    "text-center group-hover:text-foreground",
    undefined,
    (value) => value?.toString() ?? '0'
  ),
  store: createTextColumn<Order>(
    "store",
    "Store",
    150,
    "group-hover:text-foreground"
  ),
  shippingAddress: createTextColumn<Order>(
    "shippingAddress",
    "Shipping address",
    180,
    "group-hover:text-foreground"
  ),
  orderDate: createDateColumn<Order>("orderDate", "Created", 130),
  fulfilledDate: createDateColumn<Order>("fulfilledDate", "Fulfilled", 130),
  canceledDate: createDateColumn<Order>("canceledDate", "Canceled", 130),
}

const DEFAULT_ORDER_COLUMNS = ["id", "totalAmount", "status", "description", "itemCount", "store", "shippingAddress", "orderDate", "fulfilledDate", "canceledDate"] as const

export const getOrderColumns = (): Column<Order>[] => {
  return DEFAULT_ORDER_COLUMNS.map(key => ORDER_COLUMNS[key]).filter(Boolean)
}

export const getOrderColumnConfig = (): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(DEFAULT_ORDER_COLUMNS)
  return Object.entries(ORDER_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["id", "status"].includes(key),
    order: index
  }))
}